import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CloseRounded from "@ucs/Icons/CloseRounded";
import SearchIcon from "@ucs/Icons/Search";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    drawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
    location: getLocation(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    setNavDrawerIsOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    flexContainer?: InjectableCss;
    logoContainer?: InjectableCss;
    navContainer?: InjectableCss;
    menuContainer?: InjectableCss;
    searchContainer?: InjectableCss;

    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    toastMessage?: TypographyPresentationProps;
    toastLinkText?: LinkPresentationProps;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
    },
    container: {
        css: css`
            background-color: white;
            -webkit-box-shadow: 0px 1px 24px 5px rgba(0, 0, 0, 0.32);
            box-shadow: 0px 1px 24px 5px rgba(0, 0, 0, 0.32);
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 1rem 25px;
                    `,
                    css`
                        padding: 1rem 25px;
                    `,
                    css`
                        padding: 1rem 35px;
                    `,
                    css`
                        padding: 1rem 35px;
                    `,
                    css`
                        padding: 1rem 45px;
                    `,
                ]);
            }}
        `,
    },
    flexContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            flex-wrap: nowrap;
            height: 45px;
        `,
    },
    logoContainer: {
        css: css`
            display: inline-flex;
            div,
            span,
            a {
                /*Hack to fix vertical centering of logo*/
                display: inline-flex;
                img {
                    height: 45px;
                }
            }

            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        order: 2;
                        flex-grow: 1;
                    `,
                    css`
                        order: 2;
                        flex-grow: 1;
                    `,
                    css`
                        order: 2;
                        flex-grow: 1;
                    `,
                    css`
                        order: 2;
                        flex-grow: 1;
                    `,
                    css`
                        order: 1;
                    `,
                ]);
            }}
        `,
    },
    navContainer: {
        css: css`
            flex-grow: 1;

            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        order: 1;
                        flex-grow: 0;
                        margin-right: 25px;
                    `,
                    css`
                        order: 1;
                        flex-grow: 0;
                        margin-right: 25px;
                    `,
                    css`
                        order: 1;
                        flex-grow: 0;
                        margin-right: 25px;
                    `,
                    css`
                        order: 1;
                        flex-grow: 0;
                        margin-right: 25px;
                    `,
                    css`
                        order: 2;
                    `,
                ]);
            }}
        `,
    },
    menuContainer: {
        css: css`
            display: flex;
            order: 3;
            align-items: center;
            margin-left: 20px;
            gap: 20px;
            > div {
                height: 24px;
                > a {
                    height: 24px;
                    width: 24px;
                    padding: 0px;
                }
            }
            > span:first-child {
                color: ${getColor("primary")};
                cursor: pointer;
                &:hover {
                    color: #000;
                }
            }
        `,
    },
    searchContainer: {
        css: css`
            width: 100%;
            flex-basis: 100%;
            margin-top: 15px;
            order: 4;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 10px;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    toastMessage: {
        css: css`
            font-size: 15px;
            font-weight: 400;
        `,
    },
    toastLinkText: {
        typographyProps: {
            css: css`
                font-size: 15px;
                font-weight: 400;
                text-decoration: underline;
            `,
        },
    },
};

const Seperator = styled.div`
    height: 28px;
    width: 2px;
    background-color: ${UCSColors.gray07};
`;

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    drawerIsOpen,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    setNavDrawerIsOpen,
    location,
}: Props) => {
    const [showSearch, setShowSearch] = useState(false);
    const [showHeader, setShowHeader] = useState(false);
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const toggleSearch = () => {
        if (!showSearch) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
        }
        setShowSearch(!showSearch);
    };

    const closeDrawer = () => {
        if (drawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        }
    };

    useEffect(() => {
        if (drawerIsOpen && showSearch) {
            setShowSearch(false);
        }
    });

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl !== undefined) {
            toaster.addToast({
                body: (
                    <>
                        <Typography {...styles.toastMessage}>{translate("You have new quote information.")}</Typography>
                        <Link {...styles.toastLinkText} onClick={viewMyDashboardClickHandler}>
                            {translate("View My Dashboard")}
                        </Link>
                    </>
                ),
                messageType: "success",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        // Sticky header
        let lastScrollY = 0;
        let isSticky = false;

        const unsticky = () => {
            const header = document.querySelector("header");
            if (header) {
                isSticky = false;
                header.style.transition = "none";
                header.style.position = "relative";
                document.body.style.paddingTop = "0px";
                header.style.top = "0px";
                header.style.zIndex = "auto";
            }
        };

        const sticky = () => {
            const header = document.querySelector("header");
            const headerHeight = (header?.offsetHeight || 0) + 25; // 25 is Buffer for dropshadow
            if (header && headerHeight) {
                isSticky = true;
                document.body.style.paddingTop = `${headerHeight - 25}px`;
                header.style.position = "fixed";
                header.style.transition = "top .05s linear";
                header.style.zIndex = "401"; // added to hide the popover begind the header
            }
        };

        const onScroll = (e: Event) => {
            const header = document.querySelector("header");
            // On scroll determine if sticker should be fixed or not and apply page padding to compenstate
            const headerHeight = (header?.offsetHeight || 0) + 25;

            if (header && headerHeight) {
                const scrollY = window.scrollY;
                const direction = scrollY > lastScrollY ? "down" : "up";
                // eslint-disable-next-line radix
                let headerPosition = parseInt(header.style.top.replace("px", ""));

                // If scrolling up and scrolled below the header (Not in view), sticky it
                if (direction === "up" && scrollY > 0) {
                    // Set it to fixed and start scrolling it into view
                    if (isSticky) {
                        // Gradually adjust it into position
                        if (headerPosition < 0) {
                            headerPosition += lastScrollY - scrollY;
                            headerPosition = Math.floor(headerPosition);
                            if (headerPosition > 0) {
                                headerPosition = 0;
                            }
                            header.style.top = `${headerPosition}px`;
                        }
                    } else if (scrollY > headerHeight) {
                        // Set it sticky but outside of view
                        header.style.top = `${-headerHeight}px`;
                        setTimeout(() => {
                            sticky();
                        }, 1);
                    }
                } else if (isSticky && direction === "down") {
                    // Gradually move it outside of view as they scroll down
                    headerPosition -= scrollY - lastScrollY;
                    headerPosition = Math.floor(headerPosition);

                    header.style.top = `${headerPosition}px`;

                    if (headerPosition <= -headerHeight) {
                        // Out of view, unsticky it
                        unsticky();
                    }
                } else if (scrollY <= 0) {
                    // They scrolled to top of page, unsticky it
                    unsticky();
                }
            }

            // eslint-disable-next-line no-restricted-globals
            lastScrollY = scrollY;
        };

        document.addEventListener("scroll", onScroll);
        document.body.addEventListener("touchmove", onScroll);

        setShowHeader(true);
    }, []);

    useEffect(() => {
        showSearch && location.search === "" ? setShowSearch(false) : null;
    }, [location]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {showHeader ? (
                <Page as="header" isHeader={true} {...styles.page}>
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <EagerLoadingLogoProvider value={true}>
                        <Zone contentId={id} zoneName="SecondaryNavigation" fixed />
                        <StyledWrapper {...styles.container}>
                            <StyledWrapper {...styles.flexContainer}>
                                <StyledWrapper {...styles.logoContainer} onClick={closeDrawer}>
                                    <Zone contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.navContainer}>
                                    <Zone contentId={id} zoneName="MainNavigation" fixed />
                                </StyledWrapper>
                                <StyledWrapper {...styles.menuContainer}>
                                    <Icon
                                        src={SearchIcon}
                                        onClick={() => {
                                            toggleSearch();
                                            setTimeout(() => {
                                                document
                                                    .querySelector<any>(
                                                        "[data-test-selector='headerSearchInputTextField']",
                                                    )
                                                    .focus();
                                            }, 0);
                                        }}
                                    />
                                    <StyledWrapper
                                        onClick={closeDrawer}
                                        css={css`
                                            a {
                                                height: 24px;
                                                width: 24px;
                                                padding: 0px;
                                            }
                                        `}
                                    >
                                        <Zone contentId={id} zoneName="NewCart" fixed />
                                    </StyledWrapper>
                                    <Hidden below="xl">
                                        <Seperator />
                                    </Hidden>
                                    <Hidden below="xl">
                                        <Zone contentId={id} zoneName="menu" />
                                    </Hidden>
                                </StyledWrapper>
                            </StyledWrapper>
                            {showSearch && (
                                <StyledWrapper
                                    {...styles.searchContainer}
                                    style={{ display: showSearch ? "flex" : "none" }}
                                >
                                    <SearchInput id={id} inputRef={React.createRef<HTMLInputElement>()} />
                                    <Icon
                                        src={CloseRounded}
                                        color={UCSColors.gray04}
                                        onClick={() => setShowSearch(false)}
                                        css={css`
                                            opacity: 0.8;
                                            cursor: pointer;
                                            &:hover {
                                                opacity: 1;
                                            }
                                        `}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <Zone contentId={id} zoneName="Breadcrumb" />
                    </EagerLoadingLogoProvider>
                    <span ref={afterHeader} tabIndex={-1} />
                </Page>
            ) : (
                <Page as="header" {...styles.page}></Page>
            )}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
